import './toolbar.less';

import classes from 'classnames';
import React, { FunctionComponent, ReactElement, useMemo } from 'react';
import { Menu } from 'antd';
import b_ from 'b_';

const b = b_.with('toolbar');

export interface ToolbarProps {
	title?: React.ReactNode;
	containerClass?: string;
	margin?: ToolbarMargin;
	appearance?: 'transparent' | 'elements-on-line' | 'section-header';
	containerStyle?: React.CSSProperties;
	style?: 'default' | 'dark';
	children?: React.ReactNode;
	onClick?: () => void;
	fontSize?: number;
}

export const Toolbar: FunctionComponent<ToolbarProps> = (rawProps) => {
	const props: ToolbarProps = { appearance: 'section-header', margin: ToolbarMargin.None, title: '', ...rawProps };
	const { beforeTitle, afterTitle, inTheMiddle, atTheEnd } = sortOutChildren(props);
	const containerRef = React.useRef<HTMLDivElement>(null);

	const modifiers = {
		appearance: props.appearance,
		margin: props.margin,
		style: props.style ?? 'default',
		clickable: props.onClick != null,
	};

	const onClick = React.useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			if (e.target == containerRef.current) {
				props.onClick?.();
			}
		},
		[props.onClick],
	);

	return (
		<div
			className={classes(b(modifiers), props.containerClass)}
			style={props.containerStyle}
			onClick={onClick}
			ref={containerRef}
		>
			{beforeTitle.map((child, i) => (
				<ToolbarEntry key={i}>{child}</ToolbarEntry>
			))}
			{props.title && (
				<ToolbarEntry title={true} fontSize={props.fontSize}>
					{props.title}
				</ToolbarEntry>
			)}
			{afterTitle.map((child, i) => (
				<ToolbarEntry key={i}>{child}</ToolbarEntry>
			))}
			{!!inTheMiddle.length && (
				<>
					<ToolbarEntry spacer />
					{inTheMiddle.map((child, i) => (
						<ToolbarEntry key={i}>{child}</ToolbarEntry>
					))}
				</>
			)}
			<ToolbarEntry spacer />
			{atTheEnd.map((child, i) => (
				<ToolbarEntry key={i}>{child}</ToolbarEntry>
			))}
		</div>
	);
};
interface ToolbarEntryProps {
	title?: boolean;
	spacer?: boolean;
	children?: React.ReactNode;
	fontSize?: number;
}

const ToolbarEntry: FunctionComponent<ToolbarEntryProps> = (props) => {
	const children = React.Children.toArray(props.children);
	const firstChild = (children.length > 0 ? children[0] : null) as ReactElement;

	const isToolbarSet = React.isValidElement(firstChild) && firstChild.type == ToolbarItemsSet;

	const style = useMemo(() => {
		if (!props.fontSize) {
			return null;
		}

		return {
			fontSize: props.fontSize,
		};
	}, [props.fontSize]);

	return (
		<div style={style} className={b('entry', { title: props.title, spacer: props.spacer, set: isToolbarSet })}>
			{props.children}
		</div>
	);
};

interface ToolbarItemsSetProps {
	noPadding?: boolean;
	children: React.ReactNode;
}

export const ToolbarItemsSet: FunctionComponent<ToolbarItemsSetProps> = (props) => {
	if (!props.children) return null;

	return (
		<>
			{React.Children.map(props.children, (child, i) => (
				<div key={i} className={b('set-entry', { 'no-padding': props.noPadding })}>
					{child}
				</div>
			))}
		</>
	);
};

export enum ToolbarItemPosition {
	BEFORE_TITLE = 'before-title',
	AFTER_TITLE = 'after-title',
	IN_THE_MIDDLE = 'in-the-middle',
	AT_THE_END = 'at-the-end',
}

export enum ToolbarMargin {
	Top = 'top',
	Bottom = 'bottom',
	Both = 'both',
	None = 'none',
}

function sortOutChildren(props: any) {
	const beforeTitle: any[] = [];
	const afterTitle: any[] = [];
	const inTheMiddle: any[] = [];
	const atTheEnd: any[] = [];

	React.Children.forEach(props.children, (c) => {
		if (c == null) return;

		if (typeof c === 'string') {
			atTheEnd.push(c);
		} else if (
			// c.type?.name is renamed by minification, so components are matched by reference or explicit displayName.
			// MenuButton is matched by displayName on purpose: importing it pulls the legacy jQuery chain
			// (gridMenu -> tools/utils -> controls/dialog) into standalone bundles without jQuery,
			// e.g. EnterpriseCostControl.jsp, and they crash on load.
			c.type?.displayName == 'MenuButton' ||
			c.type === Menu ||
			c.type?.displayName == 'GridMenuButton' ||
			c.props.position == ToolbarItemPosition.BEFORE_TITLE
		) {
			beforeTitle.push(c);
		} else if (c.props.position == ToolbarItemPosition.AFTER_TITLE) {
			afterTitle.push(c);
		} else if (c.props.position == ToolbarItemPosition.IN_THE_MIDDLE) {
			inTheMiddle.push(c);
		} else {
			atTheEnd.push(c);
		}
	});
	return { beforeTitle, afterTitle, inTheMiddle, atTheEnd };
}

declare module 'react' {
	interface HTMLAttributes<T> extends AriaAttributes, DOMAttributes<T> {
		// extends React's HTMLAttributes
		position?: 'after-title' | 'before-title' | 'at-the-end' | 'in-the-middle';
	}
}

// React 19's types nest the JSX namespace inside the `react` module (React.JSX) rather than the global
// scope, and only auto-populate a global one under the "react-jsx" transform. This project still uses
// the classic "jsx": "react" transform, which resolves JSX typing via the `react` module's own JSX
// namespace — so this augmentation has to target `declare module 'react'`, not `declare global`, or it
// silently stops merging in and every component quietly loses the shared `position` prop.
declare module 'react' {
	namespace JSX {
		interface IntrinsicAttributes {
			position?: 'after-title' | 'before-title' | 'at-the-end' | 'in-the-middle';
		}
	}
}
